// @viva-payout-cleanup (7 Aug 2026)
// Stripe Connect removed. Doctors are paid by monthly SEPA transfer
// from the Viva collecting account; the transfer is made in the bank
// and recorded here via markPaid. There is deliberately no automated
// payout button.
import DateInput from '../components/DateInput';
// @generated-by docnow-d4-monthly-clearings v1
// ============================================================
// MONTHLY CLEARINGS PAGE (D4)
// Cross-doctor batch view: pick a month, see all non-leaver doctors,
// generate missing clearings (bulk or per-row), mark each as paid.
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Card } from '../components/Shared';
import { billingAPI, doctorsAPI } from '../services/api';

const MONTHS_EL = [
  'Ιανουάριος','Φεβρουάριος','Μάρτιος','Απρίλιος',
  'Μάιος','Ιούνιος','Ιούλιος','Αύγουστος',
  'Σεπτέμβριος','Οκτώβριος','Νοέμβριος','Δεκέμβριος',
];

const fmtEuro = (cents) => {
  if (cents == null) return '—';
  return new Intl.NumberFormat('el-GR', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  }).format(Number(cents) / 100);
};

const StatusBadge = ({ status }) => {
  const config = {
    pending:  { label: 'Σε αναμονή',  bg: '#FEF3C7', fg: '#92400E', border: '#F59E0B' },
    paid:     { label: 'Πληρώθηκε',   bg: '#D1FAE5', fg: '#065F46', border: '#10B981' },
    failed:   { label: 'Απέτυχε',     bg: '#FEE2E2', fg: '#991B1B', border: '#DC2626' },
    on_hold:  { label: 'Σε αναστολή', bg: '#E0E7FF', fg: '#3730A3', border: '#6366F1' },
  };
  const c = config[status] || { label: status || '—', bg: '#F3F4F6', fg: '#374151', border: '#9CA3AF' };
  return (
    <span style={{
      padding: '4px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600,
      background: c.bg, color: c.fg,
      border: `1px solid ${c.border}`,
      whiteSpace: 'nowrap',
    }}>{c.label}</span>
  );
};

const NoClearingBadge = () => (
  <span style={{
    padding: '4px 10px', borderRadius: 12,
    fontSize: 11, fontWeight: 600,
    background: '#F3F4F6', color: '#6B7280',
    border: '1px dashed #9CA3AF',
    whiteSpace: 'nowrap',
  }}>Δεν έχει δημιουργηθεί</span>
);

// @iban-copy (7 Aug 2026)
// Doctors are paid by manual SEPA transfer, so a payout run needs each
// IBAN to hand. It is copied rather than displayed: a list view showing
// ten bank account numbers at once is a worse exposure than one behind
// a click, and admin access is not limited to one person.
async function copyIban(iban, name, toast) {
  if (!iban) {
    toast.error('Δεν έχει καταχωρηθεί IBAN για ' + name);
    return;
  }
  try {
    await navigator.clipboard.writeText(iban);
    toast.success('IBAN αντιγράφηκε (' + name + ')');
  } catch {
    // clipboard API needs a secure context; fall back to a prompt so the
    // admin can still select and copy manually rather than being stuck
    window.prompt('Αντιγράψτε το IBAN:', iban);
  }
}

export default function MonthlyClearingsPage() {
  const now = new Date();
  // Default to PREVIOUS month
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const [year, setYear] = useState(prevMonth.getFullYear());
  const [month, setMonth] = useState(prevMonth.getMonth() + 1);

  const [doctors, setDoctors] = useState(null);
  const [clearings, setClearings] = useState(null);
  const [loading, setLoading] = useState(false);

  // Per-row pay-form state
  const [payingClearingId, setPayingClearingId] = useState(null);
  const [payoutDate, setPayoutDate] = useState('');
  const [payoutReference, setPayoutReference] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Per-row generate state — set to doctorId while in-flight
  const [generatingDoctorId, setGeneratingDoctorId] = useState(null);

  // Bulk-generate state
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, errors: [] });

  // ── Loaders ──────────────────────────────────────────────
  const loadDoctors = useCallback(async () => {
    try {
      const res = await doctorsAPI.list();
      setDoctors(res.data?.data || []);
    } catch (err) {
      toast.error('Αποτυχία φόρτωσης ιατρών');
      setDoctors([]);
    }
  }, []);

  const loadClearings = useCallback(async () => {
    try {
      // Backend takes ?year=, no month filter — we filter client-side
      const res = await billingAPI.listClearings({ year });
      setClearings(res.data?.data || []);
    } catch (err) {
      toast.error('Αποτυχία φόρτωσης εκκαθαρίσεων');
      setClearings([]);
    }
  }, [year]);

  useEffect(() => { loadDoctors(); }, [loadDoctors]);
  useEffect(() => {
    setLoading(true);
    loadClearings().finally(() => setLoading(false));
  }, [loadClearings]);

  // ── Filtering + joining ──────────────────────────────────
  // Active doctors only (no leavers)
  const activeDoctors = (doctors || []).filter(d => !d.isLeaver);

  // Map doctorId -> clearing for THIS month
  const clearingByDoctorId = new Map();
  for (const c of (clearings || [])) {
    if (c.period_year === year && c.period_month === month) {
      clearingByDoctorId.set(c.doctor_id, c);
    }
  }

  // Build rows: one per active doctor
  const rows = activeDoctors.map(d => ({
    doctor: d,
    clearing: clearingByDoctorId.get(d.id) || null,
  }));

  // Summary
  const totalRows = rows.length;
  const generated = rows.filter(r => r.clearing != null).length;
  const missing = totalRows - generated;
  const totalDoctorShare = rows.reduce(
    (sum, r) => sum + (r.clearing?.doctor_share_cents || 0), 0
  );
  const totalPending = rows.reduce(
    (sum, r) => sum + (r.clearing?.payout_status === 'pending' ? (r.clearing?.doctor_share_cents || 0) : 0), 0
  );
  const totalPaid = rows.reduce(
    (sum, r) => sum + (r.clearing?.payout_status === 'paid' ? (r.clearing?.doctor_share_cents || 0) : 0), 0
  );

  // ── Period guards ────────────────────────────────────────
  const nowY = now.getFullYear();
  const nowM = now.getMonth() + 1;
  const isCurrentOrFuture = year > nowY || (year === nowY && month >= nowM);

  // ── Per-row generate ─────────────────────────────────────
  const handleGenerate = async (doctorId) => {
    if (isCurrentOrFuture) {
      toast.error('Επιτρέπεται μόνο για περασμένους μήνες');
      return;
    }
    setGeneratingDoctorId(doctorId);
    try {
      await billingAPI.generateClearing(doctorId, year, month);
      toast.success('✅ Εκκαθάριση δημιουργήθηκε');
      await loadClearings();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Αποτυχία δημιουργίας');
    } finally {
      setGeneratingDoctorId(null);
    }
  };

  // ── Bulk generate ────────────────────────────────────────
  const handleBulkGenerate = async () => {
    setShowBulkConfirm(false);
    if (isCurrentOrFuture) {
      toast.error('Επιτρέπεται μόνο για περασμένους μήνες');
      return;
    }
    // Only generate for doctors WITHOUT a clearing for this month
    const targets = rows.filter(r => !r.clearing).map(r => r.doctor);
    if (targets.length === 0) {
      toast('Όλοι οι ιατροί έχουν ήδη εκκαθάριση γι\' αυτόν τον μήνα');
      return;
    }

    setBulkRunning(true);
    setBulkProgress({ done: 0, total: targets.length, errors: [] });

    const errors = [];
    let done = 0;
    for (const d of targets) {
      try {
        await billingAPI.generateClearing(d.id, year, month);
      } catch (err) {
        errors.push({
          name: `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.email,
          error: err?.response?.data?.error || err.message,
        });
      }
      done += 1;
      setBulkProgress({ done, total: targets.length, errors });
    }

    setBulkRunning(false);

    if (errors.length === 0) {
      toast.success(`✅ Δημιουργήθηκαν ${done} εκκαθαρίσεις`);
    } else {
      toast.error(`Ολοκληρώθηκε με ${errors.length} σφάλματα`);
    }
    await loadClearings();
  };

  // ── Mark-paid ────────────────────────────────────────────
  const openPayForm = (clearing) => {
    setPayingClearingId(clearing.id);
    setPayoutDate(new Date().toISOString().slice(0, 10));
    setPayoutReference('');
  };
  const closePayForm = () => {
    setPayingClearingId(null);
    setPayoutDate('');
    setPayoutReference('');
  };
  const submitPay = async (clearingId) => {
    const ref = (payoutReference || '').trim();
    if (!payoutDate) { toast.error('Συμπληρώστε ημερομηνία'); return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(payoutDate)) { toast.error('Λάθος μορφή ημερομηνίας'); return; }
    if (payoutDate > new Date().toISOString().slice(0, 10)) {
      toast.error('Όχι μελλοντική ημερομηνία'); return;
    }
    if (!ref) { toast.error('Συμπληρώστε αρ. αναφοράς'); return; }

    setSubmitting(true);
    try {
      await billingAPI.markPaid(clearingId, { payoutDate, payoutReference: ref });
      toast.success('✅ Καταχωρήθηκε ως πληρωμένη');
      closePayForm();
      await loadClearings();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Αποτυχία');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Year options: this and 2 prior ───────────────────────
  const yearOptions = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <div style={{ padding: 24, maxWidth: 1280, margin: '0 auto' }}>
      {/* Period selector + bulk action */}
      <Card title="Επιλογή Μήνα">
        <div style={{ padding: 16, display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 12 }}>Μήνας</label>
            <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ padding: 10, borderRadius: 8, border: '1px solid var(--border)', minWidth: 160 }}>
              {MONTHS_EL.map((name, i) => <option key={i+1} value={i+1}>{name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 12 }}>Έτος</label>
            <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ padding: 10, borderRadius: 8, border: '1px solid var(--border)', minWidth: 100 }}>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }} />
          <button
            onClick={() => setShowBulkConfirm(true)}
            disabled={isCurrentOrFuture || missing === 0 || bulkRunning || loading}
            style={{
              background: 'var(--crimson, #C0185A)',
              color: 'white', border: 'none', borderRadius: 6,
              padding: '10px 16px', fontWeight: 600, fontSize: 13,
              cursor: (isCurrentOrFuture || missing === 0 || bulkRunning) ? 'not-allowed' : 'pointer',
              opacity: (isCurrentOrFuture || missing === 0 || bulkRunning) ? 0.5 : 1,
            }}
            title={isCurrentOrFuture ? 'Μόνο για περασμένους μήνες' : (missing === 0 ? 'Δεν λείπει καμία εκκαθάριση' : '')}
          >
            🛠️ Δημιουργία για όλους ({missing})
          </button>
        </div>
      </Card>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginTop: 16, marginBottom: 16 }}>
        <Card>
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Ενεργοί Ιατροί</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy, #1e293b)' }}>{totalRows}</div>
          </div>
        </Card>
        <Card>
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Εκκαθαρίσεις</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy, #1e293b)' }}>{generated} / {totalRows}</div>
            {missing > 0 && <div style={{ fontSize: 11, color: '#92400E', marginTop: 4 }}>Λείπουν: {missing}</div>}
          </div>
        </Card>
        <Card>
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Σύνολο για Ιατρούς</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy, #1e293b)' }}>{fmtEuro(totalDoctorShare)}</div>
          </div>
        </Card>
        <Card>
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Σε αναμονή / Πληρωμένα</div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              <span style={{ color: '#92400E' }}>{fmtEuro(totalPending)}</span>
              {' / '}
              <span style={{ color: '#065F46' }}>{fmtEuro(totalPaid)}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Bulk progress */}
      {bulkRunning && (
        <Card>
          <div style={{ padding: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 600 }}>
              Δημιουργία εκκαθαρίσεων: {bulkProgress.done} / {bulkProgress.total}
            </div>
            <div style={{ height: 8, background: '#E5E7EB', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%`,
                background: 'var(--crimson, #C0185A)',
                transition: 'width 0.3s',
              }} />
            </div>
            {bulkProgress.errors.length > 0 && (
              <div style={{ marginTop: 12, padding: 10, background: '#FEE2E2', borderRadius: 6, fontSize: 12 }}>
                ⚠️ {bulkProgress.errors.length} σφάλματα
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Main table */}
      <Card title={`Εκκαθαρίσεις · ${MONTHS_EL[month-1]} ${year}`}>
        {loading || doctors === null ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Φόρτωση...</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
            Δεν υπάρχουν ενεργοί ιατροί
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)', textAlign: 'left' }}>
                  <th style={{ padding: 12 }}>Ιατρός</th>
                  <th style={{ padding: 12, textAlign: 'center' }}>Επισκέψεις</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>Μερίδιο Ιατρού</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>Προμήθεια</th>
                  <th style={{ padding: 12, textAlign: 'center' }}>Κατάσταση</th>
                  <th style={{ padding: 12, textAlign: 'right' }}>Ενέργειες</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ doctor: d, clearing: c }) => (
                  <React.Fragment key={d.id}>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600 }}>Δρ. {d.firstName} {d.lastName}</div>
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{d.specialty || '—'}</div>
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        {c ? (c.billable_consultations ?? 0) : '—'}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>
                        {c ? fmtEuro(c.doctor_share_cents) : '—'}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right' }}>
                        {c ? fmtEuro(c.docnow_commission_cents) : '—'}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        {c ? <StatusBadge status={c.payout_status} /> : <NoClearingBadge />}
                      </td>
                      <td style={{ padding: 12, textAlign: 'right' }}>
                        {!c ? (
                          <button
                            onClick={() => handleGenerate(d.id)}
                            disabled={isCurrentOrFuture || generatingDoctorId === d.id}
                            style={{
                              background: 'transparent',
                              color: 'var(--crimson, #C0185A)',
                              border: '1px solid var(--crimson, #C0185A)',
                              borderRadius: 6,
                              padding: '6px 12px',
                              cursor: (isCurrentOrFuture || generatingDoctorId === d.id) ? 'not-allowed' : 'pointer',
                              fontWeight: 600, fontSize: 12,
                              opacity: (isCurrentOrFuture || generatingDoctorId === d.id) ? 0.5 : 1,
                            }}
                          >
                            {generatingDoctorId === d.id ? 'Δημιουργία...' : '🛠️ Δημιουργία'}
                          </button>
                        ) : c.payout_status === 'pending' ? (
                          payingClearingId === c.id ? (
                            <span style={{ fontSize: 11, color: 'var(--muted)' }}>βλ. φόρμα ↓</span>
                          ) : (
                            <>
<button
                              onClick={() => copyIban(d.iban, ((d.firstName || '') + ' ' + (d.lastName || '')).trim() || 'Ιατρός', toast)}
                              disabled={!d.iban}
                              title={d.iban ? 'Αντιγραφή IBAN στο πρόχειρο' : 'Δεν έχει καταχωρηθεί IBAN'}
                              style={{
                                background: 'white',
                                color: d.iban ? 'var(--navy, #0B1F3B)' : 'var(--muted, #9CA3AF)',
                                border: '1px solid var(--border, #E5E7EB)',
                                borderRadius: 6,
                                padding: '6px 10px',
                                cursor: d.iban ? 'pointer' : 'not-allowed',
                                fontWeight: 600, fontSize: 12,
                                marginRight: 6,
                              }}
                            >
                              📋 IBAN
                            </button>
                            <button
                              onClick={() => openPayForm(c)}
                              title="Καταχώρηση τραπεζικής μεταφοράς (SEPA)"
                              style={{
                                background: 'var(--crimson, #C0185A)',
                                color: 'white', border: 'none', borderRadius: 6,
                                padding: '6px 12px', cursor: 'pointer',
                                fontWeight: 600, fontSize: 12,
                              }}
                            >
                              💰 Καταχώρηση πληρωμής
                            </button>
                            </>
                          )
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--muted)' }}>—</span>
                        )}
                      </td>
                    </tr>
                    {/* Pay-form row */}
                    {c && payingClearingId === c.id && (
                      <tr>
                        <td colSpan={6} style={{ padding: 0 }}>
                          <div style={{ padding: 16, background: '#F9FAFB', borderBottom: '1px solid var(--border)' }}>
                            <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 13 }}>
                              Καταχώρηση πληρωμής για Δρ. {d.firstName} {d.lastName}
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto auto', gap: 10, alignItems: 'end' }}>
                              <div>
                                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, fontWeight: 600 }}>Ημερομηνία *</label>
                                <DateInput
                                  value={payoutDate}
                                  max={new Date().toISOString().slice(0, 10)}
                                  onChange={e => setPayoutDate(e.target.value)}
                                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--border)' }}
                                />
                              </div>
                              <div>
                                <label style={{ display: 'block', marginBottom: 4, fontSize: 11, fontWeight: 600 }}>Αρ. αναφοράς τράπεζας *</label>
                                <input
                                  type="text"
                                  value={payoutReference}
                                  onChange={e => setPayoutReference(e.target.value)}
                                  placeholder="π.χ. SWIFT/BIC ID ή reference τραπέζης"
                                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--border)' }}
                                />
                              </div>
                              <button
                                onClick={closePayForm}
                                disabled={submitting}
                                style={{
                                  background: 'transparent', color: 'var(--slate, #475569)',
                                  border: '1px solid var(--border)', borderRadius: 6,
                                  padding: '8px 14px', fontWeight: 600, fontSize: 12,
                                  cursor: submitting ? 'not-allowed' : 'pointer',
                                }}
                              >Ακύρωση</button>
                              <button
                                onClick={() => submitPay(c.id)}
                                disabled={submitting}
                                style={{
                                  background: 'var(--crimson, #C0185A)',
                                  color: 'white', border: 'none', borderRadius: 6,
                                  padding: '8px 14px', fontWeight: 600, fontSize: 12,
                                  cursor: submitting ? 'wait' : 'pointer',
                                  opacity: submitting ? 0.6 : 1,
                                }}
                              >{submitting ? 'Καταχώρηση...' : '✅ Επιβεβαίωση'}</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Bulk confirm modal */}
      {showBulkConfirm && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white', borderRadius: 12,
            padding: 24, maxWidth: 480, width: '90%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 12px 0' }}>Επιβεβαίωση μαζικής δημιουργίας</h3>
            <p style={{ color: '#475569', lineHeight: 1.5, marginBottom: 16 }}>
              Θα δημιουργηθούν εκκαθαρίσεις για <strong>{missing} ιατρούς</strong>{' '}
              για <strong>{MONTHS_EL[month-1]} {year}</strong>.
            </p>
            <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 20 }}>
              Η ενέργεια είναι ασφαλής (idempotent). Όσοι ιατροί έχουν ήδη εκκαθάριση
              θα παραλειφθούν αυτόματα.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowBulkConfirm(false)}
                style={{
                  background: 'transparent', color: 'var(--slate, #475569)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  padding: '10px 16px', fontWeight: 600, cursor: 'pointer',
                }}
              >Ακύρωση</button>
              <button
                onClick={handleBulkGenerate}
                style={{
                  background: 'var(--crimson, #C0185A)',
                  color: 'white', border: 'none', borderRadius: 6,
                  padding: '10px 16px', fontWeight: 600, cursor: 'pointer',
                }}
              >🛠️ Δημιουργία {missing} εκκαθαρίσεων</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

